/**
 * V181: 휴가 유형(leaveTypes) CRUD Mutations Hook
 *
 * CF 호출 기반 CRUD 기능 제공
 */

import { useState, useCallback } from 'react';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { LeaveType, DeductionType, ApprovalFlowType, LeaveUnit } from '../types/leaveType';

// ===== 타입 정의 =====

export interface LeaveTypeInput {
  code: string;
  name: string;
  description?: string;
  isPaid: boolean;
  allowedUnits: LeaveUnit[];
  deductionType: DeductionType;
  approvalFlow: ApprovalFlowType;
  requiresAttachment: boolean;
  requiresReason: boolean;
  isActive: boolean;
  /** V68: 회사별 노출 범위 (휴가 신청 화면 필터용). 미지정 시 'KR' fallback. */
  visibilityScope?: 'KR' | 'CN' | 'COMMON';
  specialRules?: LeaveType['specialRules'];
}

export interface UpdateLeaveTypeInput {
  code: string;
  updates: Partial<Omit<LeaveTypeInput, 'code'>>;
}

interface CFResponse<T = any> {
  success: boolean;
  message?: string;
  error?: string;
  data?: T;
}

// ===== Hook =====

export function useLeaveTypeMutations() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const functions = getFunctions();

  /**
   * 휴가 유형 목록 조회
   */
  const fetchLeaveTypes = useCallback(async (): Promise<LeaveType[]> => {
    try {
      setIsLoading(true);
      setError(null);

      const getLeaveTypesCF = httpsCallable<void, { success: boolean; leaveTypes: LeaveType[] }>(
        functions,
        'getLeaveTypes'
      );

      const result = await getLeaveTypesCF();

      if (!result.data.success) {
        throw new Error('휴가 유형 조회 실패');
      }

      return result.data.leaveTypes;
    } catch (err: any) {
      const errorMessage = err.message || '휴가 유형 조회 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  /**
   * 휴가 유형 생성
   */
  const createLeaveType = useCallback(async (input: LeaveTypeInput): Promise<string> => {
    try {
      setIsLoading(true);
      setError(null);

      const createLeaveTypeCF = httpsCallable<LeaveTypeInput, CFResponse<{ code: string }>>(
        functions,
        'createLeaveType'
      );

      const result = await createLeaveTypeCF(input);

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 유형 생성 실패');
      }

      return result.data.data?.code || input.code;
    } catch (err: any) {
      const errorMessage = err.message || '휴가 유형 생성 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  /**
   * 휴가 유형 수정
   */
  const updateLeaveType = useCallback(async (input: UpdateLeaveTypeInput): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const updateLeaveTypeCF = httpsCallable<UpdateLeaveTypeInput, CFResponse>(
        functions,
        'updateLeaveType'
      );

      const result = await updateLeaveTypeCF(input);

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 유형 수정 실패');
      }
    } catch (err: any) {
      const errorMessage = err.message || '휴가 유형 수정 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  /**
   * 휴가 유형 활성/비활성 토글
   */
  const toggleActive = useCallback(async (code: string): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const toggleCF = httpsCallable<{ code: string }, CFResponse<{ isActive: boolean }>>(
        functions,
        'toggleLeaveTypeActive'
      );

      const result = await toggleCF({ code });

      if (!result.data.success) {
        throw new Error(result.data.error || '상태 변경 실패');
      }

      return result.data.data?.isActive ?? false;
    } catch (err: any) {
      const errorMessage = err.message || '상태 변경 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  /**
   * 휴가 유형 삭제
   */
  const deleteLeaveType = useCallback(async (code: string): Promise<void> => {
    try {
      setIsLoading(true);
      setError(null);

      const deleteCF = httpsCallable<{ code: string }, CFResponse>(
        functions,
        'deleteLeaveType'
      );

      const result = await deleteCF({ code });

      if (!result.data.success) {
        throw new Error(result.data.error || '휴가 유형 삭제 실패');
      }
    } catch (err: any) {
      const errorMessage = err.message || '휴가 유형 삭제 중 오류가 발생했습니다.';
      setError(new Error(errorMessage));
      throw new Error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [functions]);

  return {
    fetchLeaveTypes,
    createLeaveType,
    updateLeaveType,
    toggleActive,
    deleteLeaveType,
    isLoading,
    error,
  };
}
